import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import UserCell from '../../components/ui/UserCell';
import Modal from '../../components/overlays/Modal';
import { Field } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { adminsListGet, deleteAdminUser, updateUserRoles } from '../../actions/adminAction';
import { ROLES } from './roles';
import './admin.css';

/**
 * The admin list endpoint returns everything, so filter and page client-side.
 *
 * Uses the rows the action returns rather than reading them back out of Redux: a
 * closure over the selector value would capture whatever it held on the render
 * that created the fetcher, which is before the first request has resolved.
 */
function makeFetcher(dispatch) {
  return async (params) => {
    let items = (await adminsListGet(dispatch)) || [];

    if (params.view === 'super_admins') items = items.filter((a) => a.roles?.includes('super_admin'));
    if (params.search) {
      const q = params.search.toLowerCase();
      items = items.filter((a) =>
        [a.full_name, a.email, a.country, (a.roles || []).join(' ')]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q)
      );
    }
    if (params.sort === 'member') {
      const dir = params.direction === 'desc' ? -1 : 1;
      items = [...items].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '') * dir);
    }

    const total = items.length;
    const start = (params.page - 1) * params.limit;
    return { items: items.slice(start, start + params.limit), total };
  };
}

const VIEWS = [
  { key: 'all', label: 'Everyone' },
  { key: 'super_admins', label: 'Super admins' },
];

export default function Team() {
  const dispatch = useDispatch();
  const { confirm, toast } = useOverlays();
  const activeUser = useSelector((s) => s.auth?.active_user?.data);
  const [editing, setEditing] = useState(null);
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetcher = useMemo(() => makeFetcher(dispatch), [dispatch]);

  const list = useListState({ views: VIEWS, fetcher, limit: 20 });

  const openEdit = (member) => {
    setEditing(member);
    setRoles(member.roles || []);
  };

  const toggleRole = (role) =>
    setRoles((current) =>
      current.includes(role) ? current.filter((r) => r !== role) : [...current, role]
    );

  const saveRoles = useCallback(async () => {
    setSaving(true);
    try {
      const token = JSON.parse(sessionStorage.getItem('auth'))?.accessToken;
      await updateUserRoles(dispatch, editing.id, { id: editing.id, token, roles });
      toast('Roles updated', { tone: 'success' });
      setEditing(null);
      list.reload();
    } catch {
      toast('Could not update roles', { tone: 'error' });
    } finally {
      setSaving(false);
    }
  }, [dispatch, editing, roles, toast, list]);

  const revoke = useCallback(
    async (member) => {
      const ok = await confirm({
        title: `Remove ${member.full_name}?`,
        body: `${member.full_name} (${member.email}) loses access immediately and their sign-in account is deleted. This cannot be undone.`,
        confirmLabel: 'Remove access',
        destructive: true,
      });
      if (!ok) return;
      try {
        await deleteAdminUser(member.id, dispatch);
        list.reload();
      } catch {
        /* the action already surfaced the API's reason */
      }
    },
    [confirm, dispatch, list]
  );

  const columns = useMemo(
    () => [
      {
        key: 'member',
        header: 'Member',
        width: 280,
        sortable: true,
        render: (r) => <UserCell name={r.full_name || '—'} secondary={r.email} seed={r.id} />,
      },
      { key: 'country', header: 'Country', width: 140, muted: true, render: (r) => r.country || '—' },
      {
        key: 'roles',
        header: 'Roles',
        render: (r) => (
          <span className="sv-role-chips">
            {(r.roles || []).map((role) => (
              <span className="sv-role-chip" key={role}>
                {ROLES.find((x) => x.value === role)?.label || role}
              </span>
            ))}
          </span>
        ),
      },
    ],
    []
  );

  const isSuperAdmin = activeUser?.roles?.includes('super_admin');

  return (
    <>
      <PageHeader
        section="Admin"
        title="Team"
        subtitle="Who has access to the admin panel, and what they can reach."
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.id}
        noun="members"
        selectable={false}
        searchPlaceholder="Name, email or role"
        onRowClick={openEdit}
        onRowAction={(r, e) => {
          e.stopPropagation();
          revoke(r);
        }}
        empty={{ icon: 'bx-group', title: 'No team members', description: 'Invite someone to get started.' }}
      />

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={`Roles for ${editing?.full_name || ''}`}
        size="form"
        confirmLabel="Save roles"
        confirmLoading={saving}
        confirmDisabled={roles.length === 0}
        onConfirm={saveRoles}
      >
        <p style={{ marginTop: 0 }}>
          Roles decide which screens this person sees. At least one is required.
        </p>
        <Field label="Roles">
          <div className="sv-role-grid">
            {ROLES.filter((r) => isSuperAdmin || r.value !== 'super_admin').map((role) => (
              <label className="sv-role-option" key={role.value}>
                <input
                  type="checkbox"
                  className="sv-checkbox"
                  checked={roles.includes(role.value)}
                  onChange={() => toggleRole(role.value)}
                />
                {role.label}
              </label>
            ))}
          </div>
        </Field>
      </Modal>
    </>
  );
}
